import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { supabase, checkSessionStatus } from "./supabase";
import { obtenerPermisosCliente, puedeAccederAVista } from "./permisos";
import { servicioActualizacionDiaria } from "./servicios/actualizacionDiaria";
import ReactGA from "react-ga4";
import {
  useAnalytics,
  trackNavigation,
  trackLogin,
} from "./hooks/useAnalytics";
import { useAccessControl } from "./hooks/useAccessControl";
import { usePWA } from "./hooks/usePWA";

// ✅ COMPONENTES DE AUTENTICACIÓN Y REGISTRO
import LoginProfessional from "./LoginProfessional";
import RegistroConstructora from "./RegistroConstructora";
import Payment from "./Payment";
import Onboarding from "./Onboarding";
import TestMercadoPago from "./TestMercadoPago";
import SimplePago from "./SimplePago";
import PaymentResult from "./PaymentResult";
import PaymentFailure from "./PaymentFailure";
import PaymentPending from "./PaymentPending";
import PrimerLoginEmpleado from "./PrimerLoginEmpleado";

// ✅ COMPONENTES PRINCIPALES
import Sidebar from "./Sidebar";
import FloatingSidebar from "./FloatingSidebar";
import Admin from "./Admin";
import AdminDashboard from "./AdminDashboard"; // ✅ NUEVO: Panel de administración de suscripciones
import Proyectos from "./Proyectos";
import Entradas from "./Entradas";
import Salidas from "./Salidas";
import Presupuestos from "./Presupuestos";
import PresupuestosIniciales from "./PresupuestosIniciales";
import Arqueos from "./Arqueos";
import Cambios from "./Cambios";
import Cobros from "./Cobros";
import Cajas from "./Cajas";
import Dashboard from "./Dashboard";
import HonorariosMO from "./HonorariosMO";
import FacturasComprobantes from "./FacturasComprobantes";
import Gantt from "./Gantt";
import KPITrabajadores from "./KPITrabajadores";
import SystemDemos from "./SystemDemos";
import Comisiones from "./Comisiones";
import Facturacion from "./Facturacion";
import "./App.css";
import LandingPage from "./Landing";
import AdminSuscripcionesPagos from "./AdminSuscripcionesPagos";
import NotasProyecto from "./NotasProyecto";
import CajaCliente from "./CajaCliente";

// ✅ CONFIGURACIÓN DE GOOGLE ANALYTICS
const GA_MEASUREMENT_ID =
  process.env.REACT_APP_GA_MEASUREMENT_ID || "G-XXXXXXXXXX";

// Inicializar Google Analytics
if (GA_MEASUREMENT_ID && GA_MEASUREMENT_ID !== "G-XXXXXXXXXX") {
  ReactGA.initialize(GA_MEASUREMENT_ID);
  console.log("✅ Google Analytics inicializado");
} else {
  console.log(
    "⚠️ Google Analytics no configurado - Agrega REACT_APP_GA_MEASUREMENT_ID en .env"
  );
}

// Componente interno para manejar la navegación
function AppContent({
  usuario,
  constructora,
  permisosCliente,
  isMobile,
  onLogout,
  puedeVerVista,
  ComponenteRestringido,
  onUsuarioUpdate,
}) {
  const location = useLocation();
  const navigate = useNavigate();

  // ✅ INTEGRAR GOOGLE ANALYTICS
  const { trackEvent, trackUserAction } = useAnalytics();

  // ✅ NUEVO: Hook de control de acceso
  useAccessControl(usuario, constructora);

  // ✅ PWA: Hook para detectar modo standalone y aplicar estilos
  const { isPWA, isIOS } = usePWA();

  // ✅ Función para cambiar vista usando React Router
  const handleViewChange = (newView) => {
    const currentView = getCurrentView();
    navigate(`/${newView}`);

    // Trackear navegación
    trackNavigation(currentView, newView);
  };

  // ✅ Obtener la vista actual desde la URL
  const getCurrentView = () => {
    const path = location.pathname.substring(1); // Remover el "/" inicial
    return path || "dashboard";
  };

  // Estado para colapso de sidebar
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: "#f8fafc",
        flexDirection: isMobile ? "column" : "row",
        // ✅ PWA: Aplicar estilos específicos para modo standalone
        ...(isPWA && {
          height: "100vh",
          overflow: "hidden",
          overflowX: "hidden",
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
        }),
      }}
    >
      {/* Sidebar para desktop */}
      {!isMobile && (
        <Sidebar
          constructora={constructora}
          usuario={usuario}
          currentView={getCurrentView()}
          setCurrentView={handleViewChange}
          isMobile={isMobile}
          onLogout={onLogout}
          permisosCliente={permisosCliente}
          isCollapsed={isSidebarCollapsed}
          onCollapse={setIsSidebarCollapsed}
          onUsuarioUpdate={onUsuarioUpdate}
        />
      )}

      {/* FloatingSidebar para móvil */}
      {isMobile && (
        <FloatingSidebar
          constructora={constructora}
          usuario={usuario}
          currentView={getCurrentView()}
          setCurrentView={handleViewChange}
          onLogout={onLogout}
          permisosCliente={permisosCliente}
          onUsuarioUpdate={onUsuarioUpdate}
        />
      )}

      <main
        style={{
          marginLeft: isMobile ? "0" : (isSidebarCollapsed ? "72px" : "280px"),
          marginBottom: isMobile ? "80px" : "0",
          flex: 1,
          backgroundColor: "#f8fafc",
          paddingTop: isMobile ? "10px" : "0",
          minHeight: "100vh",
          height: isPWA ? "100%" : "auto",
          boxSizing: "border-box",
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
          overscrollBehaviorY: "contain",
        }}
      >
        <Routes>
          {/* Dashboard */}
          <Route
            path="/dashboard"
            element={
              puedeVerVista("dashboard") ? (
                <Dashboard
                  constructora={constructora}
                  usuario={usuario}
                  permisosCliente={permisosCliente}
                />
              ) : (
                <ComponenteRestringido
                  vista="dashboard"
                  nombreVista="Dashboard"
                />
              )
            }
          />
          <Route
            path="/system-admin"
            element={
              puedeVerVista("system-admin") ? (
                <AdminSuscripcionesPagos />
              ) : (
                <ComponenteRestringido
                  vista="system-admin"
                  nombreVista="Panel Suscripciones & Pagos"
                />
              )
            }
          />

          {/* Cajas */}
          <Route
            path="/flujos-caja"
            element={
              puedeVerVista("flujos-caja") ? (
                <CajaCliente 
                  constructora={constructora} 
                  usuario={usuario} 
                  permisosCliente={permisosCliente}
                  location={location}
                />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/cajas"
            element={
              puedeVerVista("cajas") ? (
                usuario?.tipo_usuario === "cliente" ? (
                  <CajaCliente 
                    constructora={constructora} 
                    usuario={usuario} 
                    permisosCliente={permisosCliente}
                    location={location}
                  />
                ) : (
                  <Cajas constructora={constructora} usuario={usuario} />
                )
              ) : (
                <ComponenteRestringido vista="cajas" nombreVista="Cajas" />
              )
            }
          />

          {/* Proyectos */}
          <Route
            path="/proyectos"
            element={
              puedeVerVista("proyectos") ? (
                <Proyectos constructora={constructora} usuario={usuario} />
              ) : (
                <ComponenteRestringido
                  vista="proyectos"
                  nombreVista="Proyectos"
                />
              )
            }
          />

          {/* Honorarios y MO */}
          <Route
            path="/honorarios-mo"
            element={
              <HonorariosMO
                constructora={constructora}
                usuario={usuario}
                permisosCliente={permisosCliente}
              />
            }
          />

          {/* Entradas */}
          <Route
            path="/entradas"
            element={
              puedeVerVista("entradas") ? (
                <Entradas
                  constructora={constructora}
                  usuario={usuario}
                  permisosCliente={permisosCliente}
                />
              ) : (
                <ComponenteRestringido
                  vista="entradas"
                  nombreVista="Entradas"
                />
              )
            }
          />

          {/* Salidas */}
          <Route
            path="/salidas"
            element={
              puedeVerVista("salidas") ? (
                <Salidas
                  constructora={constructora}
                  usuario={usuario}
                  permisosCliente={permisosCliente}
                />
              ) : (
                <ComponenteRestringido vista="salidas" nombreVista="Salidas" />
              )
            }
          />

          {/* Presupuestos */}
          <Route
            path="/presupuestos"
            element={
              puedeVerVista("presupuestos") ? (
                <Presupuestos
                  constructora={constructora}
                  usuario={usuario}
                  permisosCliente={permisosCliente}
                />
              ) : (
                <ComponenteRestringido
                  vista="presupuestos"
                  nombreVista="Presupuestos"
                />
              )
            }
          />

          {/* Presupuesto Inicial */}
          <Route
            path="/presupuesto-inicial"
            element={
              puedeVerVista("presupuesto-inicial") ? (
                <PresupuestosIniciales
                  constructora={constructora}
                  usuario={usuario}
                  permisosCliente={permisosCliente}
                />
              ) : (
                <ComponenteRestringido
                  vista="presupuesto-inicial"
                  nombreVista="Presupuesto Inicial"
                />
              )
            }
          />

          {/* Arqueos */}
          <Route
            path="/arqueos"
            element={
              puedeVerVista("arqueos") ? (
                <Arqueos constructora={constructora} usuario={usuario} />
              ) : (
                <ComponenteRestringido vista="arqueos" nombreVista="Arqueos" />
              )
            }
          />

          {/* Cambios */}
          <Route
            path="/cambios"
            element={
              puedeVerVista("cambios") ? (
                <Cambios
                  constructora={constructora}
                  usuario={usuario}
                  permisosCliente={permisosCliente}
                />
              ) : (
                <ComponenteRestringido vista="cambios" nombreVista="Cambios" />
              )
            }
          />

          {/* Cobros */}
          <Route
            path="/cobros"
            element={
              puedeVerVista("cobros") ? (
                <Cobros constructora={constructora} usuario={usuario} />
              ) : (
                <ComponenteRestringido vista="cobros" nombreVista="Cobros" />
              )
            }
          />

          {/* Comisiones */}
          <Route
            path="/comisiones"
            element={
              !permisosCliente ? (
                <Comisiones
                  constructora={constructora}
                  usuario={usuario}
                  isMobile={isMobile}
                />
              ) : (
                <ComponenteRestringido
                  vista="comisiones"
                  nombreVista="Comisiones"
                />
              )
            }
          />

          {/* Admin */}
          <Route
            path="/admin"
            element={
              puedeVerVista("admin") ? (
                <Admin constructora={constructora} usuario={usuario} />
              ) : (
                <ComponenteRestringido
                  vista="admin"
                  nombreVista="Administración"
                />
              )
            }
          />

          {/* ✅ NUEVO: Panel de administración de suscripciones (solo super admins) */}
          <Route
            path="/admin-dashboard"
            element={
              puedeVerVista("system-admin") ? (
                <AdminDashboard />
              ) : (
                <ComponenteRestringido
                  vista="system-admin"
                  nombreVista="Panel de Administración"
                />
              )
            }
          />

          {/* Facturas y Comprobantes */}
          <Route
            path="/facturas-comprobantes"
            element={
              puedeVerVista("facturas-comprobantes") ? (
                <FacturasComprobantes
                  constructora={constructora}
                  usuario={usuario}
                  permisosCliente={permisosCliente}
                />
              ) : (
                <ComponenteRestringido
                  vista="facturas-comprobantes"
                  nombreVista="Facturas y Comprobantes"
                />
              )
            }
          />

          {/* Facturación */}
          <Route
            path="/facturacion"
            element={
              puedeVerVista("facturacion") ? (
                <Facturacion
                  constructora={constructora}
                  usuario={usuario}
                />
              ) : (
                <ComponenteRestringido
                  vista="facturacion"
                  nombreVista="Facturación"
                />
              )
            }
          />

          {/* Cronograma (Gantt) */}
          <Route
            path="/gantt"
            element={
              puedeVerVista("gantt") ? (
                <Gantt
                  constructora={constructora}
                  usuario={usuario}
                  permisosCliente={permisosCliente}
                />
              ) : (
                <ComponenteRestringido vista="gantt" nombreVista="Cronograma" />
              )
            }
          />

          {/* KPI Trabajadores */}
          <Route
            path="/kpi-trabajadores"
            element={
              puedeVerVista("kpi-trabajadores") ? (
                <KPITrabajadores
                  constructora={constructora}
                  usuario={usuario}
                  permisosCliente={permisosCliente}
                />
              ) : (
                <ComponenteRestringido
                  vista="kpi-trabajadores"
                  nombreVista="KPI Trabajadores"
                />
              )
            }
          />

          {/* System Demos */}
          <Route
            path="/system-demos"
            element={<SystemDemos isMobile={isMobile} />}
          />

          {/* Notas del Proyecto */}
          <Route
            path="/notas-proyecto"
            element={
              puedeVerVista("notas-proyecto") ? (
                <NotasProyecto
                  constructora={constructora}
                  usuario={usuario}
                  permisosCliente={permisosCliente}
                />
              ) : (
                <ComponenteRestringido
                  vista="notas-proyecto"
                  nombreVista="Notas del Proyecto"
                />
              )
            }
          />


          {/* Redirección por defecto */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  const [usuario, setUsuario] = useState(null);
  const [constructora, setConstructora] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // ✅ Estado para permisos de cliente
  const [permisosCliente, setPermisosCliente] = useState(null);
  const [loadingPermisos, setLoadingPermisos] = useState(false);

  // Función para actualizar el estado del usuario
  const handleUsuarioUpdate = (usuarioActualizado) => {
    setUsuario(usuarioActualizado);
  };

  useEffect(() => {
    const cleanup = verificarSesion();

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);

    // ✅ Iniciar servicio de actualización diaria
    servicioActualizacionDiaria.programarActualizacionDiaria();

    return () => {
      window.removeEventListener("resize", handleResize);
      // ✅ Limpiar subscription de auth
      if (cleanup && typeof cleanup === "function") {
        cleanup();
      }
      // ✅ Detener servicio de actualización diaria
      servicioActualizacionDiaria.detenerActualizacionDiaria();
    };
  }, []); // eslint-disable-next-line react-hooks/exhaustive-deps

  // Redirección automática al login si no hay usuario y no está cargando, solo en rutas protegidas
  useEffect(() => {
    if (!usuario && !loading) {
      const currentPath = window.location.pathname;
      const publicRoutes = ['/', '/landing', '/login', '/registro-constructora', '/pago', '/onboarding', '/test-mp', '/pago-prueba', '/success', '/failure', '/pending', '/primer-login-empleado', '/payment-failure', '/payment-pending'];
      if (!publicRoutes.includes(currentPath)) {
        window.location.href = "/login";
      }
    }
  }, [usuario, loading]);

  const verificarSesion = async () => {
    try {
      console.log("🔍 Iniciando verificación de sesión...");

      // ✅ PRIMERO: verificar si estamos en una ruta pública
      const currentPath = window.location.pathname;
      const publicRoutes = ['/', '/registro-constructora', '/pago', '/onboarding', '/login'];
      const isPublicRoute = publicRoutes.includes(currentPath);
      
      if (isPublicRoute) {
        console.log("🚫 En ruta pública, verificando sesión sin pantalla intermedia...");
        
        // ✅ Para rutas públicas: verificar sesión SIN pantalla intermedia
        const { session, error: sessionError } = await checkSessionStatus();
        
        if (sessionError) {
          console.error("❌ Error obteniendo sesión en ruta pública:", sessionError);
          setError("Error obteniendo sesión: " + sessionError.message);
          setLoading(false);
          return;
        }
        
        if (session && currentPath === '/login') {
          // ✅ Si ya está logueado y está en login → redirigir al dashboard
          console.log("🔄 Usuario ya logueado en login, redirigiendo al dashboard...");
          window.location.href = '/dashboard';
          return;
        }
        
        // ✅ Para otras rutas públicas: no restaurar sesión, continuar normalmente
        console.log("✅ Ruta pública, continuando sin restaurar sesión");
        setUsuario(null);
        setConstructora(null);
        setPermisosCliente(null);
        setLoading(false);
        return;
      }

      // ✅ Verificar sesión inicial para rutas PRIVADAS usando helper
      const { session, error: sessionError } = await checkSessionStatus();

      if (sessionError) {
        console.error("❌ Error obteniendo sesión:", sessionError);
        setError("Error obteniendo sesión: " + sessionError.message);
        setLoading(false);
        return;
      }

      if (session) {
        console.log("✅ Sesión encontrada:", session.user.email);
        await cargarUsuario();
      } else {
        console.log("❌ No hay sesión activa, verificando localStorage...");

        // ✅ Intentar recuperar desde localStorage como fallback
        try {
          const savedUser = localStorage.getItem("app_constructoras_user");
          const savedConstructora = localStorage.getItem(
            "app_constructoras_constructora"
          );
          const loginTime = localStorage.getItem(
            "app_constructoras_login_time"
          );

          if (savedUser && savedConstructora && loginTime) {
            const userData = JSON.parse(savedUser);
            const constructoraData = JSON.parse(savedConstructora);
            const loginTimestamp = parseInt(loginTime);
            const now = Date.now();
            const hoursSinceLogin = (now - loginTimestamp) / (1000 * 60 * 60);

            console.log("📦 Datos encontrados en localStorage:", {
              userEmail: userData.email,
              hoursSinceLogin: hoursSinceLogin.toFixed(2),
            });

            // ✅ Si han pasado menos de 24 horas, restaurar sesión
            if (hoursSinceLogin < 24) {
              console.log("🔄 Restaurando sesión desde localStorage...");
              setUsuario(userData);
              setConstructora(constructoraData);

              // ✅ Cargar permisos si es cliente
              if (userData.tipo_usuario === "cliente") {
                setLoadingPermisos(true);
                try {
                  const permisos = await obtenerPermisosCliente(
                    userData,
                    constructoraData
                  );
                  setPermisosCliente(permisos);
                } catch (permisosError) {
                  console.error("Error cargando permisos:", permisosError);
                }
                setLoadingPermisos(false);
              }

              setError(null);
              setLoading(false);
              return;
            } else {
              console.log(
                "⏰ Sesión expirada (más de 24 horas), limpiando localStorage"
              );
              localStorage.removeItem("app_constructoras_user");
              localStorage.removeItem("app_constructoras_constructora");
              localStorage.removeItem("app_constructoras_login_time");
            }
          }
        } catch (err) {
          console.error("❌ Error recuperando desde localStorage:", err);
        }

        // ✅ Asegurar que usuario sea null cuando no hay sesión
        setUsuario(null);
        setConstructora(null);
        setPermisosCliente(null);
        setLoading(false);
      }

      // ✅ Escuchar cambios en la autenticación DESPUÉS
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange(async (event, session) => {
        console.log("🔄 Auth state changed:", event, session?.user?.email);

        if (event === "SIGNED_IN" && session) {
          console.log("🔐 Usuario logueado, cargando datos...");
          await cargarUsuario();
        } else if (event === "SIGNED_OUT") {
          console.log("🚪 Usuario deslogueado");
          setUsuario(null);
          setConstructora(null);
          setPermisosCliente(null);
          setLoading(false);
        } else if (event === "TOKEN_REFRESHED" && session) {
          console.log("🔄 Token refreshed successfully");
        } else if (event === "USER_UPDATED" && session) {
          console.log("👤 Usuario actualizado");
        }
      });

      // ✅ Limpiar subscription al desmontar
      return () => {
        console.log("🧹 Limpiando subscription de auth");
        subscription?.unsubscribe();
      };
    } catch (err) {
      console.error("❌ Error verificando sesión:", err);
      setError("Error verificando sesión: " + err.message);
      setLoading(false);
    }
  };

  const cargarUsuario = async () => {
    try {
      console.log("👤 Iniciando carga de usuario...");

      // Obtener datos completos del usuario
      const { data: userData, error: userError } = await supabase.rpc(
        "get_usuario_by_auth"
      );

      console.log("📊 Resultado get_usuario_by_auth:", {
        hasData: !!userData,
        dataLength: userData?.length,
        hasError: !!userError,
        error: userError?.message,
      });

      if (userError) throw userError;

      if (userData && userData.length > 0) {
        const userInfo = userData[0];
        console.log("✅ Usuario cargado:", userInfo.email);

        // ✅ Guardar en localStorage para persistencia
        localStorage.setItem(
          "app_constructoras_user",
          JSON.stringify(userInfo)
        );
        localStorage.setItem(
          "app_constructoras_login_time",
          Date.now().toString()
        );

        setUsuario(userInfo);

        // ✅ Cargar constructora asociada
        if (userInfo.constructora_id) {
          const { data: constructoraData, error: constructoraError } =
            await supabase
              .from("constructoras")
              .select("*")
              .eq("id", userInfo.constructora_id)
              .single();

          if (constructoraError) {
            console.error("❌ Error cargando constructora:", constructoraError);
            setError(
              "Error cargando constructora: " + constructoraError.message
            );
          } else {
            console.log("✅ Constructora cargada:", constructoraData.nombre);
            localStorage.setItem(
              "app_constructoras_constructora",
              JSON.stringify(constructoraData)
            );
            setConstructora(constructoraData);

            // ✅ Cargar permisos si es cliente
            if (userInfo.tipo_usuario === "cliente") {
              setLoadingPermisos(true);
              try {
                const permisos = await obtenerPermisosCliente(
                  userInfo,
                  constructoraData
                );
                setPermisosCliente(permisos);
              } catch (permisosError) {
                console.error("Error cargando permisos:", permisosError);
              }
              setLoadingPermisos(false);
            }
          }
        }

        setError(null);
      } else {
        console.log("❌ No se encontraron datos de usuario");
        setError("No se encontraron datos de usuario");
      }
    } catch (err) {
      console.error("❌ Error cargando usuario:", err);
      setError("Error cargando usuario: " + err.message);
    }
    setLoading(false);
  };

  const handleLoginSuccess = async (datosLogin) => {
    try {
      // ✅ Trackear login exitoso
      trackLogin("email");

      // Flujo viejo: setear usuario y constructora directamente
      setUsuario(datosLogin.usuario);
      setConstructora(datosLogin.constructora);
      localStorage.setItem(
        "app_constructoras_user",
        JSON.stringify(datosLogin.usuario)
      );
      localStorage.setItem(
        "app_constructoras_constructora",
        JSON.stringify(datosLogin.constructora)
      );
      localStorage.setItem(
        "app_constructoras_login_time",
        Date.now().toString()
      );

      // Si es cliente, cargar permisos
      if (datosLogin.usuario.tipo_usuario === "cliente") {
        setLoadingPermisos(true);
        try {
          const permisos = await obtenerPermisosCliente(
            datosLogin.usuario,
            datosLogin.constructora
          );
          setPermisosCliente(permisos);
        } catch (permisosError) {
          console.error("Error cargando permisos:", permisosError);
        }
        setLoadingPermisos(false);
      }

      setError(null);
      setLoading(false);

      // ✅ Redirigir al dashboard tras login exitoso
      try {
        if (typeof window !== "undefined") {
          window.location.href = "/dashboard";
        }
      } catch (redirError) {
        console.error("Error al redirigir después del login:", redirError);
      }
    } catch (err) {
      console.error("❌ Error en handleLoginSuccess:", err);
      setError("Error procesando login: " + err.message);
    }
  };

  const handleLogout = async () => {
    try {
      console.log("🚪 Iniciando logout...");
      await supabase.auth.signOut();

      // ✅ Limpiar localStorage también
      localStorage.removeItem("app_constructoras_user");
      localStorage.removeItem("app_constructoras_constructora");
      localStorage.removeItem("app_constructoras_login_time");
      console.log("🧹 localStorage limpiado");

      setUsuario(null);
      setConstructora(null);
      setPermisosCliente(null);
    } catch (err) {
      console.error("Error en logout:", err);
    }
  };

  // ✅ Función para verificar acceso a vista
  const puedeVerVista = (vista) => {
    const isCliente = usuario?.tipo_usuario === "cliente";
    const isConstructoraOrEmpleado =
      usuario?.tipo_usuario === "constructora" ||
      usuario?.tipo_usuario === "empleado";

    // Constructoras y empleados pueden ver todo
    if (isConstructoraOrEmpleado) return true;

    // Clientes solo según permisos
    if (isCliente && permisosCliente) {
      return puedeAccederAVista(permisosCliente.permisos, vista);
    }

    return false;
  };

  // ✅ Componente para mostrar restricciones
  const ComponenteRestringido = ({ vista, nombreVista }) => {
    if (!permisosCliente) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "20px",
              padding: isMobile ? "40px 30px" : "60px 50px",
              boxShadow:
                "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
              border: "1px solid #e2e8f0",
              maxWidth: "400px",
              width: "100%",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: "80px",
                height: "80px",
                background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 30px",
                boxShadow: "0 10px 15px -3px rgba(59, 130, 246, 0.3)",
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  border: "3px solid white",
                  borderTop: "3px solid transparent",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
            </div>

            <h2
              style={{
                color: "#1e293b",
                fontSize: isMobile ? "20px" : "24px",
                fontWeight: "600",
                marginBottom: "12px",
                marginTop: "0",
              }}
            >
              Verificando permisos
            </h2>

            <p
              style={{
                color: "#64748b",
                fontSize: "14px",
                marginBottom: "30px",
                lineHeight: "1.5",
              }}
            >
              Estamos validando tu acceso a esta sección...
            </p>

            <div
              style={{
                width: "100%",
                height: "4px",
                background: "#e2e8f0",
                borderRadius: "2px",
                overflow: "hidden",
                marginBottom: "20px",
              }}
            >
              <div
                style={{
                  width: "60%",
                  height: "100%",
                  background:
                    "linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)",
                  borderRadius: "2px",
                  animation: "pulse 2s ease-in-out infinite",
                }}
              />
            </div>

            <p
              style={{
                color: "#64748b",
                fontSize: "12px",
                margin: "0",
                fontStyle: "italic",
              }}
            >
              Esto puede tomar unos segundos
            </p>
          </div>

          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              
              @keyframes pulse {
                0%, 100% { opacity: 0.6; }
                50% { opacity: 1; }
              }
            `}
          </style>
        </div>
      );
    }

    const tipoObra =
      permisosCliente.tipoProyecto === "llave_en_mano"
        ? "Llave en Mano"
        : "por Administración";

    return (
      <div
        style={{
          padding: isMobile ? "30px 20px" : "50px 40px",
          textAlign: "center",
          background: "white",
          margin: "20px",
          borderRadius: "16px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "2px solid #fbbf24",
        }}
      >
        <div style={{ fontSize: "64px", marginBottom: "24px" }}>🔒</div>
        <h2
          style={{
            color: "#dc2626",
            marginBottom: "16px",
            fontSize: isMobile ? "20px" : "24px",
          }}
        >
          Acceso Restringido
        </h2>
        <p
          style={{
            color: "#374151",
            marginBottom: "24px",
            fontSize: "16px",
            lineHeight: "1.5",
          }}
        >
          No tienes permisos para acceder a <strong>{nombreVista}</strong>.
        </p>
        <p
          style={{
            color: "#6b7280",
            fontSize: "14px",
            lineHeight: "1.5",
          }}
        >
          Tu proyecto es de tipo <strong>{tipoObra}</strong> y solo puedes
          acceder a las secciones habilitadas para tu tipo de contrato.
        </p>
      </div>
    );
  };

  // ✅ Pantalla de carga (solo para rutas privadas)
  const currentPath = window.location.pathname;
  const publicRoutes = ['/', '/registro-constructora', '/pago', '/onboarding', '/login'];
  const isPublicRoute = publicRoutes.includes(currentPath);
  
  if (loading && !isPublicRoute) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
          padding: "20px",
        }}
      >
        <div
          style={{
            textAlign: "center",
            background: "white",
            padding: isMobile ? "40px 30px" : "60px 50px",
            borderRadius: "16px",
            border: "1px solid #f1f5f9",
            maxWidth: "400px",
            width: "100%",
          }}
        >
          <div
            style={{
              width: "32px",
              height: "32px",
              border: "2px solid #e2e8f0",
              borderTop: "2px solid #3b82f6",
              borderRadius: "50%",
              margin: "0 auto 16px",
              animation: "spin 1s linear infinite",
            }}
          />

          <h2
            style={{
              color: "#1e293b",
              fontSize: isMobile ? "18px" : "20px",
              fontWeight: "600",
              marginBottom: "8px",
              marginTop: "0",
            }}
          >
            Cargando aplicación
          </h2>

          <p
            style={{
              color: "#64748b",
              fontSize: "14px",
              margin: 0,
              fontWeight: "500",
            }}
          >
            Verificando tu sesión...
          </p>
        </div>


      </div>
    );
  }

  // ✅ Pantalla de error
  if (error) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
          padding: "20px",
        }}
      >
        <div
          style={{
            textAlign: "center",
            background: "white",
            padding: "40px",
            borderRadius: "24px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            border: "1px solid #fecaca",
            maxWidth: "500px",
          }}
        >
          <div
            style={{
              fontSize: "56px",
              marginBottom: "24px",
              background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            ⚠️
          </div>
          <h2
            style={{
              fontSize: "24px",
              color: "#dc2626",
              margin: "0 0 16px 0",
              fontWeight: "700",
            }}
          >
            Error de Autenticación
          </h2>
          <p
            style={{
              fontSize: "16px",
              color: "#64748b",
              margin: "0 0 32px 0",
              lineHeight: "1.5",
            }}
          >
            {error}
          </p>
          <button
            onClick={() => {
              setError(null);
              verificarSesion();
            }}
            style={{
              background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
              color: "white",
              border: "none",
              padding: "14px 28px",
              borderRadius: "12px",
              fontSize: "15px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "transform 0.2s ease",
              boxShadow: "0 4px 12px rgba(239, 68, 68, 0.3)",
            }}
          >
            🔄 Reintentar
          </button>
        </div>
      </div>
    );
  }

  // ✅ Pantalla de carga de permisos para clientes
  if (loadingPermisos) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
          padding: "20px",
        }}
      >
        <div
          style={{
            textAlign: "center",
            background: "white",
            padding: isMobile ? "40px 30px" : "60px 50px",
            borderRadius: "20px",
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            border: "1px solid #e2e8f0",
            maxWidth: "400px",
            width: "100%",
          }}
        >
          <div
            style={{
              width: "80px",
              height: "80px",
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 30px",
              boxShadow: "0 10px 15px -3px rgba(16, 185, 129, 0.3)",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                border: "3px solid white",
                borderTop: "3px solid transparent",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
          </div>

          <h2
            style={{
              color: "#1e293b",
              fontSize: isMobile ? "20px" : "24px",
              fontWeight: "600",
              marginBottom: "12px",
              marginTop: "0",
            }}
          >
            Verificando permisos
          </h2>

          <p
            style={{
              color: "#64748b",
              fontSize: "14px",
              marginBottom: "30px",
              lineHeight: "1.5",
            }}
          >
            Configurando tu acceso personalizado...
          </p>

          <div
            style={{
              width: "100%",
              height: "4px",
              background: "#e2e8f0",
              borderRadius: "2px",
              overflow: "hidden",
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                width: "60%",
                height: "100%",
                background: "linear-gradient(90deg, #10b981 0%, #059669 100%)",
                borderRadius: "2px",
                animation: "pulse 2s ease-in-out infinite",
              }}
            />
          </div>

          <p
            style={{
              color: "#64748b",
              fontSize: "12px",
              margin: "0",
              fontStyle: "italic",
            }}
          >
            Configurando permisos específicos...
          </p>
        </div>

        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            
            @keyframes pulse {
              0%, 100% { opacity: 0.6; }
              50% { opacity: 1; }
            }
          `}
        </style>
      </div>
    );
  }

  // ✅ NUEVO SISTEMA DE RUTAS COMPLETO
  return (
    <Router>
      <Routes>
        {/* =================== RUTAS PÚBLICAS (sin autenticación) =================== */}

        {/* Landing principal */}
        <Route path="/" element={<LandingPage />} />

        {/* Redirección de /landing a / */}
        <Route path="/landing" element={<Navigate to="/" replace />} />

        {/* Ruta de prueba para Mercado Pago - TEMPORAL */}
        <Route path="/test-mp" element={<TestMercadoPago />} />

        {/* Ruta de pago simple de prueba - TEMPORAL */}
        <Route path="/pago-prueba" element={<SimplePago />} />

        {/* Rutas de respuesta de pago */}
        <Route path="/success" element={<PaymentResult />} />
        <Route path="/failure" element={<PaymentResult />} />
        <Route path="/pending" element={<PaymentResult />} />

        {/* =================== RUTAS DE AUTENTICACIÓN =================== */}

        {/* Login profesional estilo Stripe */}
        <Route
          path="/login"
          element={
            <LoginProfessional
              onLoginSuccess={handleLoginSuccess}
              onNavigateToClientFirstTime={() =>
                (window.location.href = "/cliente-primera-vez")
              }
            />
          }
        />

        {/* Primer login de empleado */}
        <Route
          path="/primer-login-empleado"
          element={<PrimerLoginEmpleado />}
        />

        {/* =================== RUTAS DE REGISTRO =================== */}

        {/* Registro de constructora (multistep) */}
        <Route
          path="/registro-constructora"
          element={
            <RegistroConstructora
              onNavigateToPayment={(datosRegistro) => {
                // Redirigir a /pago pasando los datos por estado de navegación
                window.location.href = `/pago?email=${encodeURIComponent(datosRegistro.email_empleado)}`;
              }}
              onNavigateBack={() => (window.location.href = "/")}
            />
          }
        />

        {/* Pantalla de pago */}
        <Route
          path="/pago"
          element={
            <Payment
              onPaymentSuccess={() => {
                // Redirigir a onboarding tras pago exitoso
                window.location.href = "/onboarding";
              }}
              onNavigateBack={() =>
                (window.location.href = "/registro-constructora")
              }
            />
          }
        />

        {/* Onboarding inicial */}
        <Route
          path="/onboarding"
          element={
            <Onboarding
              onOnboardingComplete={handleLoginSuccess}
              onNavigateToLogin={() => (window.location.href = "/login")}
            />
          }
        />
        <Route path="/payment-failure" element={<PaymentFailure />} />
        <Route path="/payment-pending" element={<PaymentPending />} />

        {/* =================== RUTAS PROTEGIDAS (requieren autenticación) =================== */}

        {/* Si el usuario está logueado, mostrar la app principal */}
        {usuario ? (
          <Route
            path="/*"
            element={
              <AppContent
                usuario={usuario}
                constructora={constructora}
                permisosCliente={permisosCliente}
                isMobile={isMobile}
                onLogout={handleLogout}
                onUsuarioUpdate={handleUsuarioUpdate}
                puedeVerVista={puedeVerVista}
                ComponenteRestringido={ComponenteRestringido}
              />
            }
          />
        ) : null}
        {/* Eliminar la redirección automática a login para rutas no encontradas */}
      </Routes>
    </Router>
  );
}

export default App;
